/* ==========================================================================
   Cart summary (total items, subtotal, savings, total): keeps BOTH the
   header cart drawer's summary block AND the full /cart page's summary
   block live after the page loads.

   Originally this only targeted #HeaderCart (the drawer). That meant
   quantity changes made with the +/- stepper on the full /cart page
   updated the drawer's own (hidden, off-screen) copy and the shared
   [data-subtotal] "Total" row (still driven by cart-form.js, unchanged),
   but never refreshed "Total items" / "Subtotal" in the /cart page's own
   summary card added in snippets/section-main-cart.liquid — that block
   just sat at its server-rendered values until a full page reload. Fixed
   here by applying the same render logic to every matching container on
   the page, not just #HeaderCart.

   Still standalone / not wired into cart-form.js or any theme module, for
   the same reason as before: cart-form.js's CartForm class is shared
   between the drawer and the /cart page (both use the same [data-subtotal]
   / [data-discounts] selectors), so this keeps living as an independent
   script that only ever touches the data-summary-* elements it owns.

   THE SAME MATH AS THE INITIAL SERVER-RENDERED VALUES, kept live after
   the page loads. Both header-cart-drawer.liquid and section-main-cart.liquid
   compute "Savings" and the pre-discount "Subtotal" from each cart line's
   item.variant.compare_at_price vs. item.final_price (matching the
   "SAVE $x" badge logic already used on product pages and in
   cart-item.liquid) rather than from cart.total_discount /
   cart.original_total_price — confirmed live against this store's real
   cart that those Shopify fields only reflect cart-level discount codes /
   automatic discounts, not a Compare-at markdown, and this store's Spring
   Sale pricing is Compare-at-based with no such cart-level discount
   active, so those fields alone would show $0 savings on a normal cart.

   /cart.js (the standard Shopify AJAX Cart API used here) exposes the
   same per-line fields as the cart Liquid object (original_line_price,
   final_price, quantity, handle, variant_id, ...) EXCEPT
   variant.compare_at_price — that's only on the product/variant object,
   not the cart. So this script fetches each unique product's
   /products/{handle}.js once per refresh cycle (cached in memory per
   handle for the life of the page, so a cart with several lines of the
   same product, or repeated drawer opens, triggers only one request per
   distinct product) to get that one missing figure, then reproduces the
   same sum the Liquid blocks do.

   Updates on the same real events already used elsewhere in this cart
   (confirmed genuine DOM CustomEvents via assets/pubsub.js and
   assets/cart-drawer.js / cart-form.js in earlier fixes to this cart):
     - CartDrawer:open   (drawer opened)
     - ajaxProduct:added (item added via quick-add / product form)
     - cart:updated      (quantity changed via qty stepper or remove,
                           fired on BOTH the drawer's and the /cart page's
                           stepper — confirmed cart-form.js's CartForm
                           dispatches this regardless of which instance
                           triggered the change)
     - cart:build        (theme's own manual "rebuild cart" event)

   Loaded as an ES module (see the <script type="module"> tag in both
   header-cart-drawer.liquid and section-main-cart.liquid) specifically so
   it can import the theme's own formatMoney from
   @archetype-themes/scripts/helpers/currency — already in the site's
   import map, so this needs no importmap changes.
   ========================================================================== */
import { formatMoney } from '@archetype-themes/scripts/helpers/currency'

;(function () {
  // Every container with a [data-cart-summary] block gets kept in sync:
  // today that's #HeaderCart (the drawer, always in the DOM) and
  // #CartPageForm (the /cart page, only present on that page). Using a
  // shared class instead of hardcoding ids means a future third summary
  // instance (if one's ever added) is picked up automatically too.
  var containers = Array.prototype.slice.call(
    document.querySelectorAll('#HeaderCart, #CartPageForm')
  )
  if (!containers.length) return

  function cartUrl(path) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'
    return root.replace(/\/$/, '') + path
  }

  // handle -> Promise<Map<variant_id, compare_at_price>>, shared across
  // every container so a product common to both the drawer and the page
  // (always true, since they show the same cart) is only fetched once.
  var productCache = {}

  function getVariantCompareAtMap(handle) {
    if (productCache[handle]) return productCache[handle]

    productCache[handle] = fetch(cartUrl('/products/' + handle + '.js'), { credentials: 'same-origin' })
      .then(function (response) { return response.json() })
      .then(function (product) {
        var map = {}
        ;(product.variants || []).forEach(function (variant) {
          map[variant.id] = variant.compare_at_price || null
        })
        return map
      })
      .catch(function () {
        return {}
      })

    return productCache[handle]
  }

  function renderInto(container, data, compareAtByHandle) {
    var summary = container.querySelector('[data-cart-summary]')
    if (!summary) return

    var itemsEl = container.querySelector('[data-summary-items]')
    var discountRowEl = container.querySelector('[data-summary-discount-row]')
    var discountEl = container.querySelector('[data-summary-discount]')
    var subtotalEl = container.querySelector('[data-summary-subtotal]')

    // Same money_format and superscript setting this container's own form
    // was rendered with (data-money-format / data-super-script on the
    // <form>), so figures here match the theme's own currency formatting
    // exactly — each container reads its own form's dataset rather than
    // assuming both use the same one.
    var form = container.querySelector('form')
    var moneyFormat = (form && form.dataset.moneyFormat) || undefined
    var superScript = form && form.dataset.superScript === 'true'

    function money(cents) {
      return formatMoney(cents, moneyFormat, superScript)
    }

    if (!data) return

    var itemCount = data.item_count || 0

    // Mirrors header-cart-drawer.liquid's / section-main-cart.liquid's
    // summary_pre_discount_subtotal / summary_discount computation
    // exactly: start from each line's original_line_price
    // (post-cart-level-discount, pre-markdown-adjustment), then add the
    // Compare-at markdown on top where one applies.
    var preDiscountSubtotal = 0
    var markdownDiscount = 0

    ;(data.items || []).forEach(function (item) {
      var compareAtMap = compareAtByHandle[item.handle] || {}
      var compareAt = compareAtMap[item.variant_id]
      var originalLinePrice = item.original_line_price || 0
      var quantity = item.quantity || 0

      preDiscountSubtotal += originalLinePrice

      // Skip free-gift lines (final_price 0): their value is already in
      // data.total_discount, so adding their Compare-at markdown too
      // double-counted it (the "$10 out" bug). Compare against
      // original_price so a cart-level discount isn't counted twice.
      // Keep in sync with the Liquid loops in header-cart-drawer.liquid
      // and section-main-cart.liquid.
      if (compareAt && item.final_price > 0 && compareAt > (item.original_price || 0)) {
        var compareAtLineTotal = compareAt * quantity
        var lineMarkdown = compareAtLineTotal - originalLinePrice
        preDiscountSubtotal += lineMarkdown
        markdownDiscount += lineMarkdown
      }
    })

    var totalDiscount = markdownDiscount + (data.total_discount || 0)

    summary.style.display = itemCount > 0 ? '' : 'none'

    if (itemsEl) itemsEl.textContent = itemCount

    if (discountRowEl) {
      discountRowEl.style.display = totalDiscount > 0 ? '' : 'none'
    }
    if (discountEl && totalDiscount > 0) {
      // formatMoney() returns an HTML string (this theme's money_format
      // includes markup, e.g. "<span class=money>...</span>" — confirmed
      // live), so this needs innerHTML, not textContent, matching how
      // cart-form.js's own this.subtotal.innerHTML = formatMoney(...)
      // already does it elsewhere in the cart.
      discountEl.innerHTML = '-' + money(totalDiscount)
    }

    if (subtotalEl) subtotalEl.innerHTML = money(preDiscountSubtotal)

    // Item count in the drawer title (drawer only; null-safe on /cart)
    var countLabelEl = container.querySelector('[data-summary-count-label]')
    if (countLabelEl) {
      countLabelEl.textContent = '(' + itemCount + (itemCount === 1 ? ' item)' : ' items)')
      countLabelEl.style.display = itemCount > 0 ? '' : 'none'
    }

    // Deliberately not touching [data-subtotal] ("Total") here —
    // cart-form.js already keeps that element live on every cart change,
    // in both the drawer and the /cart page, and cart.total_price already
    // correctly reflects each line's real (marked-down) price, needing no
    // Compare-at adjustment.
  }

  function refresh() {
    fetch(cartUrl('/cart.js'), { credentials: 'same-origin' })
      .then(function (response) { return response.json() })
      .then(function (data) {
        var uniqueHandles = []
        ;(data.items || []).forEach(function (item) {
          if (uniqueHandles.indexOf(item.handle) === -1) uniqueHandles.push(item.handle)
        })

        return Promise.all(
          uniqueHandles.map(function (handle) {
            return getVariantCompareAtMap(handle).then(function (map) {
              return [handle, map]
            })
          })
        ).then(function (pairs) {
          var compareAtByHandle = {}
          pairs.forEach(function (pair) {
            compareAtByHandle[pair[0]] = pair[1]
          })
          containers.forEach(function (container) {
            renderInto(container, data, compareAtByHandle)
          })
        })
      })
      .catch(function () {
        // Network hiccup: leave whatever was last rendered (including the
        // server-rendered initial values from Liquid) rather than blanking
        // the summary out.
      })
  }

  document.addEventListener('CartDrawer:open', refresh)
  document.addEventListener('ajaxProduct:added', refresh)
  document.addEventListener('cart:updated', refresh)
  document.addEventListener('cart:build', refresh)
})()